import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import { formatSignificanceClause } from "types/significance.types"
import {
  Catalogue,
  CatalogueInstance,
  CatalogueQuestion,
  findQuestion,
} from "../types/catalogue"
import {
  Banner,
  isMeanStat,
  isMeanStatistic,
  MEAN_EXCLUDING_ZERO,
  MeanStat,
  SiblingLayout,
  SortRule,
  TableSpec,
  TabSpecDocument,
} from "../types/tabSpecDocument"
import { boundsProblem, rangeExpression } from "../model/conditions"
import { isYesNoField } from "../model/dataVariables"
import { RangeItem, rangeWarnings } from "../model/ranges"
import { ResolvedTable, resolveTable } from "../model/resolve"
import { boxMembers, isNps, labelsInRange, middleMembers } from "../model/scale"
import { ShownNames, shownNames } from "../model/shownLabels"
import {
  canSummarise,
  inSlice,
  isNumberQuestion,
  legacySummary,
  slicedRowName,
  sliceWords,
  summaryName,
} from "../model/summaries"
import { BannerPlan, compileBanner } from "./banner"
import { compileBase } from "./base"
import {
  inLoadedData,
  nameKey,
  ref,
  refusedName,
  TableNames,
  tableNames,
  TOTAL_TABLE,
  unsafeLabel,
  unsafeRef,
  unsafeText,
} from "./names"
import { scoreExpression, scoreFieldType } from "./scores"

/**
 * "check": who in the base has no category of a table from the data marked,
 * or several. "mean-excl-0": the mean parts of the scores or values with
 * every 0 left out.
 */
export type OutputRole =
  | "main"
  | "check"
  | "mean"
  | "mean-total"
  | "mean-excl-0"
  | "mean-excl-0-total"

/** One displayed row (or block of statistic rows), in display order. */
export type RowSlot =
  /**
   * The Totals block of the main output, shown as "Base". `total`: the main
   * output is the total table (a grid summary), whose one row is the base.
   * `stats`: the base of a number table's rows, who are the people with a
   * value: when the engine drops the Totals (one row left), the statistics'
   * own base ("base n", and "base N" when weighted) is shown instead.
   */
  | { kind: "base"; total?: true; stats?: true }
  /**
   * A row of the main output. Put back with zeros when the engine dropped
   * it, except a value of a number (`value`, each value as a row): nobody
   * gave it, and it is left out (spec D10); and an answer row of a table
   * that hides the rows nobody gave (`TablePlan.hideEmpty`). `fixed`: the
   * row keeps its place at the end of its run when the rows are sorted (an
   * exception, an answer kept at the end, a range or a yes or no row).
   */
  | {
      kind: "row"
      label: string
      display: string
      net: boolean
      value?: true
      fixed?: boolean
    }
  /**
   * One statistic taken from the mean outputs. `field` null = the DTAB had
   * one field. `excludingZero`: taken from the mean parts without the zeros
   * (the mean excluding 0). `bases`: a grid row's own base statistics, shown
   * above the value ("base n", and "base N" when weighted). `sum`: a sum
   * summary's "total" (`field` null: the sum of every row's sum, worked out
   * here), or a "row", whose sum is followed by its share of the Total.
   */
  | {
      kind: "stat"
      field: string | null
      stat: STATISTIC_TYPE
      display: string
      excludingZero?: true
      bases?: string[]
      sum?: "total" | "row"
    }
  /**
   * `plus` minus `minus` (names of net rows), on the column percentage, in
   * points. `fields`: `plus` and `minus` are fields of the mean outputs
   * instead, minus each other on their `perc` (a grid row's NPS), with the
   * row's own `bases` above.
   */
  | {
      kind: "difference"
      display: string
      plus: string
      minus: string
      fields?: true
      bases?: string[]
    }

/** One sibling of a grid of the data shown as one table: where its figures are, and its rows. */
export interface SiblingPart {
  /** `CatalogueInstance.id` of the sibling. */
  id: string
  /** The name the table shows it under: its shown label, or its raw label. */
  display: string
  /**
   * Positions in `TablePlan.outputs`, and so in the engine's response, of
   * the sibling's own outputs: its main table, its check and its mean parts
   * (null: the sibling has none), and the mean parts without the zeros
   * (absent when the sibling shows no mean excluding 0).
   */
  outputs: {
    main: number
    check: number | null
    mean: number | null
    meanTotal: number | null
    meanExcl0?: number
    meanExcl0Total?: number | null
  }
  /** Its rows, as the table of this sibling alone lays them out. */
  layout: RowSlot[]
  /** As `TablePlan.check`, for this sibling. */
  check: { kind: "categorical" | "scale" } | null
}

/**
 * A grid of the data whose rows are sibling containers, shown as one table:
 * the table of each sibling, run under that sibling's own paths (so each has
 * its own base), all in the one script.
 */
export interface SiblingPlan {
  layout: SiblingLayout
  /** Layout "row": the row each sibling shows (`slotNamed` finds it). null for "stacked". */
  row: string | null
  /** The siblings the table shows, in instance order. */
  parts: SiblingPart[]
}

export interface TablePlan {
  tableId: string
  status: "run" | "skip"
  skipReason?: string
  /** One request. Empty when skipped. */
  script: string
  /** What `result.output[i]` holds, in order. */
  outputs: OutputRole[]
  /** Every name the script creates. After a failure each is removed in a request of its own. */
  temps: string[]
  banner: BannerPlan | null
  layout: RowSlot[]
  significance: boolean
  title: string
  baseText: string
  filterText: string | null
  weightText: string
  warnings: string[]
  /**
   * A table from the data shown as single choice or as a scale: the engine
   * counts who has no category marked, or several (output "check"), and the
   * finished table says so. null everywhere else, skipped tables included.
   */
  check: { kind: "categorical" | "scale" } | null
  /**
   * The order of the finished table's answer rows (a grid summary's rows),
   * worked out once the table is built (`runtime/arrange.ts`).
   */
  sort: SortRule
  /** The answer rows nobody gave, and a grid summary's rows nobody answered, are left out. */
  hideEmpty: boolean
  /**
   * Set for a grid of the data whose rows are sibling containers shown as
   * one table (`showsSiblings`); `layout` is then empty and `check` null:
   * each sibling has its own. Absent on every other table.
   */
  siblings?: SiblingPlan
}

/**
 * Whether `table` shows the grid `q`, whose rows are sibling containers, as
 * one table: no instance of its own, as single or multiple choice. A table
 * of one sibling (one instance) is an ordinary table.
 */
export const showsSiblings = (
  table: Pick<TableSpec, "instance" | "kind">,
  q: Pick<CatalogueQuestion, "siblings">
): boolean =>
  q.siblings !== undefined &&
  table.instance === null &&
  (table.kind === "categorical" || table.kind === "multi")

/** The name of the mean, and its name when the mean excluding 0 is shown under it. */
export const MEAN_NAME = "Mean"
export const MEAN_INCLUDING_ZERO_NAME = "Mean, including 0"

/** Whether `s` is a mean shown as "Mean, including 0". */
const isIncludingZero = (s: RowSlot): boolean =>
  s.kind === "stat" && s.stat === STATISTIC_TYPE.AVG && s.display === MEAN_INCLUDING_ZERO_NAME

/**
 * The slot the name of a chosen row names in `layout`: an option by its raw
 * label, a net by its name, a difference row (or the mean) by the name it is
 * shown under; "Mean" also names the mean shown as "Mean, including 0".
 * undefined when there is none.
 */
export const slotNamed = (layout: RowSlot[], name: string): RowSlot | undefined =>
  layout.find((s) =>
    s.kind === "row"
      ? s.label === name
      : s.kind !== "base" && (s.display === name || (name === MEAN_NAME && isIncludingZero(s)))
  )

/**
 * The sentences the tables of several siblings said, as the one table says
 * them: one that every sibling said, once; any other after the name of each
 * sibling that said it ("Bank 3: ..."). With one sibling, every sentence is
 * named.
 */
export const siblingSentences = (said: { display: string; list: string[] }[]): string[] => {
  const common =
    said.length > 1
      ? [...new Set(said[0].list)].filter((s) => said.every((p) => p.list.includes(s)))
      : []
  return [
    ...common,
    ...said.flatMap((p) =>
      [...new Set(p.list)].filter((s) => !common.includes(s)).map((s) => `${p.display}: ${s}`)
    ),
  ]
}

/** The slots a scale leaves out when its scores cannot be trusted: the mean and every difference row. */
export const isScoreSlot = (s: RowSlot): boolean =>
  (s.kind === "stat" && s.field === null) || s.kind === "difference"

/** The names of the score slots of `slots`: "mean" first, then each difference by its display name. */
const scoreNames = (slots: RowSlot[]): string[] => [
  ...(slots.some((s) => s.kind === "stat" && s.field === null) ? ["mean"] : []),
  ...slots.flatMap((s) => (s.kind === "difference" ? [s.display] : [])),
]

/** "a", "a and b", "a, b and c" (`last` "or": "a or b"). */
const listOf = (items: string[], last: "and" | "or"): string =>
  items.length === 1 ? items[0] : `${items.slice(0, -1).join(", ")} ${last} ${items[items.length - 1]}`

/**
 * The rows a scale leaves out when its scores cannot be trusted, as a
 * sentence starts: "The mean", "NPS", "The mean and NPS", "The mean, NPS and
 * Gap" (differences by their display name), and the verb that follows. null
 * when `slots` holds none of them.
 */
export const leftOutRows = (slots: RowSlot[]): { what: string; verb: "is" | "are" } | null => {
  const items = scoreNames(slots)
  if (items.length === 0) return null
  const words = items.map((item, i) => (i === 0 && item === "mean" ? "The mean" : item))
  return { what: listOf(words, "and"), verb: items.length === 1 ? "is" : "are" }
}

/** "mean", "mean or NPS", "NPS": what cannot be worked out of the score slots of `slots`. */
const noScoreWords = (slots: RowSlot[]): string => listOf(scoreNames(slots), "or")

export const XTAB_STATS: STATISTIC_TYPE[] = [
  STATISTIC_TYPE.N,
  STATISTIC_TYPE.BIG_N,
  STATISTIC_TYPE.PERC_PERCENT,
  STATISTIC_TYPE.INDEX,
  STATISTIC_TYPE.ROW_PERCENT,
  STATISTIC_TYPE.COL_PERCENT,
]
/** The name each row under a mean is shown under. */
export const MEAN_STAT_NAMES: Record<MeanStat, string> = {
  [MEAN_EXCLUDING_ZERO]: "Mean, excluding 0",
  [STATISTIC_TYPE.MEDIAN]: "Median",
  [STATISTIC_TYPE.STDDEV]: "Standard deviation",
  [STATISTIC_TYPE.STDERR]: "Standard error",
}
/**
 * The statistics a numeric table can show, in display order. The mean
 * excluding 0 (`meanStats`) goes right after the mean.
 */
export const NUMERIC_STATS: { stat: STATISTIC_TYPE; display: string }[] = [
  { stat: STATISTIC_TYPE.BASE_N, display: "Base" },
  { stat: STATISTIC_TYPE.AVG, display: MEAN_NAME },
  { stat: STATISTIC_TYPE.MEDIAN, display: MEAN_STAT_NAMES[STATISTIC_TYPE.MEDIAN] },
  { stat: STATISTIC_TYPE.STDDEV, display: MEAN_STAT_NAMES[STATISTIC_TYPE.STDDEV] },
  { stat: STATISTIC_TYPE.STDERR, display: MEAN_STAT_NAMES[STATISTIC_TYPE.STDERR] },
  { stat: STATISTIC_TYPE.MIN, display: "Minimum" },
  { stat: STATISTIC_TYPE.MAX, display: "Maximum" },
]
/**
 * What a numeric table shows when its list names none of `NUMERIC_STATS`:
 * the four it showed before there were statistics under the mean.
 */
const NUMERIC_FALLBACK = NUMERIC_STATS.filter((s) => !isMeanStat(s.stat))
/** What a grid summary of means can show for each grid row: the mean and the statistics under it. */
export const MEAN_SUMMARY_STATS = NUMERIC_STATS.filter(
  (s) => s.stat === STATISTIC_TYPE.AVG || isMeanStat(s.stat)
)
/**
 * What each grid row of a summary of means shows, in display order: those of
 * `statistics` it can show, or the mean alone when there are none.
 */
export const meanSummaryStats = (
  statistics: STATISTIC_TYPE[]
): { stat: STATISTIC_TYPE; display: string }[] => {
  const picked = MEAN_SUMMARY_STATS.filter((s) => statistics.includes(s.stat))
  return picked.length > 0 ? picked : MEAN_SUMMARY_STATS.slice(0, 1)
}
const MEAN_STATS = [STATISTIC_TYPE.BASE_N, STATISTIC_TYPE.AVG]
/** The weighted base of a DTAB, which no table of the dashboards asks for. */
export const WEIGHTED_BASE = "base N"
/** The first row of a sum summary: the sum of every row's sum. */
export const SUM_TOTAL_NAME = "Total"
/** The two shares an NPS summary takes of each grid row: scored 9 to 10, and 0 to 6. */
const NPS_PROMOTERS = "Promoters"
const NPS_DETRACTORS = "Detractors"

/** The widest span of values (highest minus lowest) a number table shows value by value (spec D10). */
export const MOST_VALUE_SPAN = 250

/** The slot of the mean excluding 0: the mean of the mean parts without the zeros. */
const excludingZeroSlot = (): RowSlot => ({
  kind: "stat",
  field: null,
  stat: STATISTIC_TYPE.AVG,
  display: MEAN_STAT_NAMES[MEAN_EXCLUDING_ZERO],
  excludingZero: true,
})

const operators = (stats: string[]): string =>
  ` OPERATORS: ${stats.map((s) => `"${s}"`).join(", ")}`

/** Why an option could not go into the script. */
type Loss = "gone" | "unsafe" | "not-loaded"
const LOSS: Record<Loss, string> = {
  gone: "no longer exists",
  unsafe: "has a character the engine cannot take",
  "not-loaded": "is not in the loaded data",
}

/** The codebook path of one option of `instance`, or why the script cannot use it. */
const optionPath = (
  instance: CatalogueInstance,
  label: string,
  catalogue: Catalogue
): { path: string } | { loss: Loss } => {
  const path = instance.optionPaths[label]
  if (!path) return { loss: "gone" }
  if (unsafeLabel(label) || unsafeText(path)) return { loss: "unsafe" }
  if (!inLoadedData(catalogue, path)) return { loss: "not-loaded" }
  return { path }
}

/** Label to path of the options of `instance` that the script can use. */
const usablePaths = (
  instance: CatalogueInstance,
  catalogue: Catalogue
): Record<string, string> =>
  Object.fromEntries(
    Object.keys(instance.optionPaths).flatMap((label) => {
      const found = optionPath(instance, label, catalogue)
      return "path" in found ? [[label, found.path]] : []
    })
  )

/** The options `labels` of `instance` that the script can use, in order, and those it cannot. */
const pickOptions = (
  instance: CatalogueInstance,
  labels: string[],
  catalogue: Catalogue
): { used: { label: string; path: string }[]; lost: { label: string; loss: Loss }[] } => {
  const used: { label: string; path: string }[] = []
  const lost: { label: string; loss: Loss }[] = []
  labels.forEach((label) => {
    const found = optionPath(instance, label, catalogue)
    if ("path" in found) used.push({ label, path: found.path })
    else lost.push({ label, loss: found.loss })
  })
  return { used, lost }
}

/** Why a grid row was left out when none of its options (`what`) can be used. */
const rowLeftOut = (what: string, lost: { loss: Loss }[]): string =>
  lost.some((l) => l.loss === "unsafe")
    ? `has a ${what} with a character the engine cannot take`
    : lost.some((l) => l.loss === "not-loaded")
      ? `has no ${what} in the loaded data`
      : lost.length > 0
        ? `has no ${what} that still exists`
        : `has no ${what}`

const ALL_LEFT_OUT = "Every row of this table was left out."

/** Why a net was left out when none of its options can be used. */
const netLeftOut = (lost: Loss[]): string =>
  lost.includes("unsafe")
    ? "has an option with a character the engine cannot take"
    : lost.includes("not-loaded")
      ? "is not in the loaded data"
      : lost.length > 0
        ? "has no option that still exists"
        : "has no options"

/**
 * Why a banner with groups has no column left, in one sentence with its
 * name: the run dialog and the Excel contents sheet show the reason alone.
 * It is the loaded data when the banner would have columns with every path
 * loaded, and the prepared data when a group from the data names a variable
 * the preparation no longer makes.
 */
const bannerSkipReason = (banner: Banner, catalogue: Catalogue, names: ShownNames): string => {
  if (compileBanner(banner, { ...catalogue, knownPaths: null }, names).name !== TOTAL_TABLE)
    return `Banner "${banner.name}" has no column in the loaded data.`
  const dataGone =
    catalogue.knownPaths !== null &&
    banner.groups.some((g) => g.source === "data" && !findQuestion(catalogue, g.variable, "data"))
  return dataGone
    ? `Banner "${banner.name}" has no column in the prepared data.`
    : `Banner "${banner.name}" has no column that can be used.`
}

/** Why a table from the data has no question: where its variable went. */
const dataSkipReason = (variable: string, catalogue: Catalogue, names: ShownNames): string => {
  const shown = names.variable(variable)
  return findQuestion(catalogue, variable)
    ? `${shown} is a data variable, but the questionnaire now has a question with that name.`
    : catalogue.knownPaths
      ? `${shown} is not in the prepared data.`
      : `${shown} comes from the data, which is not loaded yet.`
}

/**
 * Every leaf of `instance` as the check's sum, hidden rows and unscored
 * categories included, or null when one of them cannot go into the script
 * (a partial sum would pass respondents it never saw). Plain, a missing leaf
 * makes the sum missing (who answered); `all` counts it as not marked
 * (facts.md, fact 11).
 */
const checkSum = (instance: CatalogueInstance, catalogue: Catalogue, all: boolean): string | null => {
  const paths: string[] = []
  for (const label of Object.keys(instance.optionPaths)) {
    const found = optionPath(instance, label, catalogue)
    if (!("path" in found)) return null
    paths.push(found.path)
  }
  if (paths.length === 0) return null
  return paths.map((p) => (all ? `(${ref(p)} ? 1 : 0)` : ref(p))).join(" + ")
}

/**
 * `names`: the shown labels the title, the rows, the columns, the base and
 * filter lines and every sentence use (the spec's own by default;
 * `compileSpecTable` passes one that records what it read). They never reach
 * the engine: the script, the outputs and the temporary names are those of
 * the raw labels.
 */
export const compileTable = (
  resolved: ResolvedTable,
  spec: TabSpecDocument,
  catalogue: Catalogue,
  names: ShownNames = shownNames(spec.labels)
): TablePlan => {
  const { table } = resolved
  const weightText = spec.data.weight
    ? `Weighted by ${names.variable(spec.data.weight)}`
    : "Unweighted"
  const skip = (skipReason: string, kept: string[] = []): TablePlan => ({
    tableId: table.id,
    status: "skip",
    skipReason,
    script: "",
    outputs: [],
    temps: [],
    banner: null,
    layout: [],
    significance: false,
    title: resolved.title,
    baseText: "",
    filterText: null,
    weightText,
    warnings: kept,
    check: null,
    sort: resolved.sort,
    hideEmpty: resolved.hideEmpty,
  })

  const q = resolved.question
  if (!q)
    return skip(
      table.source === "data"
        ? dataSkipReason(table.question, catalogue, names)
        : `${names.variable(table.question)} is no longer in the questionnaire.`
    )
  if (table.kind === "text") return skip("Text answers are not tabulated.")
  const variable = names.variable(q.label)
  /** A grid row, a numeric cell or a loop iteration, as the sentences name it. */
  const instanceName = (i: CatalogueInstance): string => names.instance(q.label, i.id, i.label)
  // A grid whose rows are sibling containers, shown as one table: a table
  // per sibling, each under its own paths.
  const siblingTable = showsSiblings(table, q)
  if (table.kind !== "grid-summary" && !siblingTable && !resolved.instance) {
    const gone = table.instance ?? ""
    return skip(`${variable} no longer has "${gone ? names.instance(q.label, gone, gone) : gone}".`)
  }
  if (!resolved.banner)
    return skip("The banner this table used was deleted. Choose another banner.")
  const instances: CatalogueInstance[] =
    table.kind === "grid-summary" || siblingTable
      ? q.instances
      : [resolved.instance as CatalogueInstance]
  const present = catalogue.knownPaths ? instances.filter((i) => i.inData) : instances
  if (present.length === 0) return skip(`${variable} is not in the loaded data.`)

  const banner = compileBanner(resolved.banner, catalogue, names)
  const base = compileBase(resolved, catalogue, names, spec.filter)
  const n = tableNames(table.id)
  const totalOnly = banner.name === TOTAL_TABLE
  const warnings = [...banner.problems, ...base.warnings]
  if (base.skipReason) return skip(base.skipReason, warnings)
  // A banner with groups gives the table its columns; with none left the
  // table would run on the Total column alone, which is not what it asks for.
  if (totalOnly && resolved.banner.groups.length > 0)
    return skip(bannerSkipReason(resolved.banner, catalogue, names), warnings)
  const sig = totalOnly ? "" : formatSignificanceClause(resolved.significance)
  const clause = base.expression ? ` UNIVERSE:${ref(n.universe)}` : ""
  const universe = base.expression
    ? [`FIELD ${ref(n.universe)} BOOL`, `CALC ${ref(n.universe)} "${base.expression}"`]
    : []
  const order = q.options.map((o) => o.label)
  /** The exceptions of the question ("Don't know"): rows that are never sorted. */
  const exceptions = new Set(q.options.flatMap((o) => (o.exception ? [o.label] : [])))
  // Under the mean: the mean excluding 0, then the engine's statistics.
  const excl0 = resolved.meanStats.includes(MEAN_EXCLUDING_ZERO)
  const meanStatistics = resolved.meanStats.filter(isMeanStatistic)

  const lines: string[] = [...universe]
  const temps: string[] = base.expression ? [n.universe] : []
  const outputs: OutputRole[] = []
  const layout: RowSlot[] = []
  const cleanup: string[] = []
  let title = resolved.title
  let baseText = base.baseText
  let tested = false
  let check: TablePlan["check"] = null
  let siblings: SiblingPlan | undefined
  /** The SIG clause, for a statistic that is tested. */
  const sigFor = (test: boolean): string => {
    if (!test || !sig) return ""
    tested = true
    return sig
  }

  /**
   * The main XTAB, under the names `own` (the table's, or one sibling's),
   * within the table's universe or the clause `universe` gives.
   */
  const mainTable = (
    own: TableNames,
    rowsName: string,
    stats: STATISTIC_TYPE[],
    test: boolean,
    universe: string = clause
  ): void => {
    lines.push(
      `XTAB ${own.result} ${rowsName} ${banner.name}${universe}${operators(stats)}${sigFor(test)}`,
      `OUTPUT ${own.result}`,
      `REMOVE ${own.result}`
    )
    temps.push(own.result)
    outputs.push("main")
  }
  /** The mean parts, under the names `own`. */
  const meanTables = (
    own: TableNames,
    fields: string,
    stats: string[],
    test: boolean
  ): void => {
    lines.push(
      `DTAB ${own.mean} ${banner.name} ${fields}${clause}${operators(stats)}${sigFor(test)}`,
      `OUTPUT ${own.mean} /T`,
      `REMOVE ${own.mean}`
    )
    temps.push(own.mean)
    outputs.push("mean")
    if (totalOnly) return
    // A DTAB has no Totals: the mean of everyone comes from a DTAB over the total table.
    lines.push(
      `DTAB ${own.meanTotal} ${TOTAL_TABLE} ${fields}${clause}${operators(stats)}`,
      `OUTPUT ${own.meanTotal} /T`,
      `REMOVE ${own.meanTotal}`
    )
    temps.push(own.meanTotal)
    outputs.push("mean-total")
  }
  /**
   * The mean excluding 0, under the names `own`: `value` (the score field or
   * the number) with every 0 made missing, its mean tested like the mean, and
   * its Totals as the mean parts get theirs.
   */
  const meanExcl0Tables = (own: TableNames, value: string): void => {
    const field = ref(own.excl0)
    lines.push(`FIELD ${field} REAL`, `CALC ${field} "${value} == 0 ? missing : ${value}"`)
    temps.push(own.excl0)
    cleanup.push(own.excl0)
    const stats = operators(MEAN_STATS)
    lines.push(
      `DTAB ${own.meanExcl0} ${banner.name} ${field}${clause}${stats}${sigFor(true)}`,
      `OUTPUT ${own.meanExcl0} /T`,
      `REMOVE ${own.meanExcl0}`
    )
    temps.push(own.meanExcl0)
    outputs.push("mean-excl-0")
    if (totalOnly) return
    lines.push(
      `DTAB ${own.meanExcl0Total} ${TOTAL_TABLE} ${field}${clause}${stats}`,
      `OUTPUT ${own.meanExcl0Total} /T`,
      `REMOVE ${own.meanExcl0Total}`
    )
    temps.push(own.meanExcl0Total)
    outputs.push("mean-excl-0-total")
  }
  const xtabStats = (): STATISTIC_TYPE[] => {
    const chosen = resolved.statistics.filter(
      (s) => XTAB_STATS.includes(s) && (s !== STATISTIC_TYPE.BIG_N || !!spec.data.weight)
    )
    const stats = chosen.length > 0 ? chosen : [STATISTIC_TYPE.COL_PERCENT, STATISTIC_TYPE.N]
    if (sig && !stats.includes(STATISTIC_TYPE.COL_PERCENT)) stats.push(STATISTIC_TYPE.COL_PERCENT)
    // A weighted table shows both bases: the count and the weighted count.
    if (spec.data.weight && stats.includes(STATISTIC_TYPE.N) && !stats.includes(STATISTIC_TYPE.BIG_N))
      stats.push(STATISTIC_TYPE.BIG_N)
    return stats
  }

  /**
   * One instance shown as single choice, multiple choice or a scale: its
   * rows, its main table, its check and its mean parts, written into the
   * script under the names `own`; its sentences go to `said`. Returns its
   * layout and its check, or why it cannot be shown (nothing is written then).
   */
  const choiceTable = (
    instance: CatalogueInstance,
    own: TableNames,
    said: string[]
  ): { layout: RowSlot[]; check: TablePlan["check"] } | { skip: string } => {
    const slots: RowSlot[] = []
    let checked: TablePlan["check"] = null
    // Every leaf of a grid row from the data sits under the row's own label:
    // one the engine cannot take is named once, not as each of its leaves.
    // The label is never the last segment of a leaf's path, so a trailing `>`
    // is fine there (`<<Grid//Row>//x>>`).
    if (table.source === "data" && instance.label && unsafeText(instance.label))
      return {
        skip: `${variable}: "${instanceName(instance)}" has a character the engine cannot take.`,
      }
    const fromData = table.source === "data"
    // What the codebook says of the fields of a variable from the data: at
    // most one is marked ("single"), several may be ("multi"), or nothing
    // (an engine that does not say).
    const answers = fromData ? q.answers : undefined
    // The codebook gives the type when the data is loaded, and a preparation
    // can write into the fields afterwards without changing it (facts.md,
    // fact 17): "single" holds as it is only while there is no preparation.
    const prepared = spec.data.pipelines.length > 0
    const choice = table.kind === "categorical" || table.kind === "scale"
    /** Why a variable the codebook calls "multi" gets no score, naming what went. */
    const noScores = (gone: RowSlot[]): string =>
      `${variable} is a set of yes or no fields, so no ${noScoreWords(gone)} can be worked out.`
    const used = new Set<string>()
    const nets = new Set<string>()
    /** The names the rows kept so far are shown under. */
    const shown: string[] = []
    // The engine keeps one leaf per label: an option keeps its row over a net
    // of the same name (its label or its rename, in any case), wherever the
    // net stands.
    const optionRows = new Set(
      resolved.rows.flatMap((r) =>
        r.kind === "option" && !r.hidden
          ? [nameKey(r.label), nameKey(r.rename?.trim() || r.label)]
          : []
      )
    )
    /**
     * The names a shown label gives way to, wherever they stand: the raw
     * label and the rename of each option, and the name of each net and each
     * difference row. A shown label never reaches the engine, and a display
     * name never costs a row its place.
     */
    const rowNames = resolved.rows.flatMap((r): { label: string | null; name: string }[] => {
      if (r.kind === "net" || r.kind === "difference") return [{ label: null, name: r.name.trim() }]
      if (r.kind !== "option" || r.hidden) return []
      const rename = r.rename?.trim()
      return [r.label, ...(rename ? [rename] : [])].map((name) => ({ label: r.label, name }))
    })
    const rowLines: string[] = []
    let wantsMean = false
    // A mean scored by the first true field is wrong when several can be
    // true. Single choice and scales from the data are dealt with below (the
    // check, or what the codebook says); on multiple choice the row is left
    // out unless the codebook's "single" holds, and on single choice when
    // nothing says the fields exclude each other. The table runs.
    const meanRefused =
      fromData &&
      table.kind !== "scale" &&
      resolved.rows.some((r) => r.kind === "mean") &&
      (table.kind === "multi" ? !(answers === "single" && !prepared) : answers === undefined)
    if (meanRefused)
      said.push(
        answers === "multi"
          ? noScores([{ kind: "stat", field: null, stat: STATISTIC_TYPE.AVG, display: "Mean" }])
          : `${variable} is a set of yes or no fields from the data, so no mean can be worked out.`
      )
    slots.push({ kind: "base" })

    resolved.rows.forEach((r) => {
      if (r.kind === "option") {
        if (r.hidden) return
        const named = names.option(q.label, r.label)
        const found = optionPath(instance, r.label, catalogue)
        if ("loss" in found) {
          said.push(`"${named}" ${LOSS[found.loss]} and was left out.`)
          return
        }
        if (used.has(r.label)) {
          said.push(`"${named}" is used twice, so the second was left out.`)
          return
        }
        // A rename first, then the shown label, then the raw label. A shown
        // label that cannot be used (it holds a line break, or another row
        // has the name) gives way to the raw label; a rename that cannot be
        // used leaves the row out, as the raw label does.
        const rename = r.rename?.trim()
        let display = rename || r.label
        let shownUsed = false
        if (!rename && named !== r.label) {
          const others = [
            ...shown,
            ...rowNames.filter((o) => o.label !== r.label).map((o) => o.name),
          ]
          const refused = refusedName(named, others, "row", "display")
          if (refused)
            said.push(`The shown label "${named}" of "${r.label}" was not used. ${refused}`)
          else {
            display = named
            shownUsed = true
          }
        }
        if (!shownUsed) {
          const refused = refusedName(display, shown)
          if (refused) {
            said.push(`"${rename ? named : r.label}" was left out. ${refused}`)
            return
          }
        }
        used.add(r.label)
        shown.push(display)
        rowLines.push(`APPEND ${own.rows} ${ref(found.path)}`)
        // An exception ("Don't know") and an answer kept at the end stay at
        // the end of their run when the rows are sorted.
        const fixed = r.anchor === true || exceptions.has(r.label)
        slots.push({ kind: "row", label: r.label, display, net: false, ...(fixed ? { fixed } : {}) })
      } else if (r.kind === "net") {
        const name = r.name.trim()
        if (used.has(name) || (name && optionRows.has(nameKey(name)))) {
          said.push(`The net "${name}" has the name of another row and was left out.`)
          return
        }
        const refused = refusedName(name, shown)
        if (refused) {
          said.push(`The net "${name}" was left out. ${refused}`)
          return
        }
        const { used: members, lost } = pickOptions(instance, r.members, catalogue)
        if (members.length === 0) {
          said.push(
            `The net "${name}" ${netLeftOut(lost.map((l) => l.loss))} and was left out.`
          )
          return
        }
        lost.forEach(({ label, loss }) =>
          said.push(
            `"${names.option(q.label, label)}" ${LOSS[loss]}, so the net "${name}" was built without it.`
          )
        )
        used.add(name)
        nets.add(name)
        shown.push(name)
        const field = ref(`${own.rows}//${name}`)
        rowLines.push(`FIELD ${field} BOOL false`)
        members.forEach((m) => rowLines.push(`OR ${field} ${ref(m.path)}`))
        slots.push({ kind: "row", label: name, display: name, net: true })
      } else if (r.kind === "difference") {
        const name = r.name.trim()
        const refused = refusedName(name, shown)
        if (refused) {
          said.push(`The difference "${name}" was left out. ${refused}`)
          return
        }
        const plus = r.plus.trim()
        const minus = r.minus.trim()
        // Each side is a row kept above it: a net by its name, or an option
        // by its label (the engine's name of its row, which the result reads).
        if (!used.has(plus) || !used.has(minus)) {
          const named = (row: string): string =>
            q.options.some((o) => o.label === row) ? names.option(q.label, row) : row
          said.push(
            `"${name}" needs the rows "${named(plus)}" and "${named(minus)}" above it and was left out.`
          )
          return
        }
        shown.push(name)
        slots.push({ kind: "difference", display: name, plus, minus })
      } else if (r.kind === "mean" && !wantsMean && !meanRefused) {
        wantsMean = true
        slots.push({
          kind: "stat",
          field: null,
          stat: STATISTIC_TYPE.AVG,
          display: excl0 ? MEAN_INCLUDING_ZERO_NAME : MEAN_NAME,
        })
        // What is shown under the mean comes and goes with it.
        if (excl0) slots.push(excludingZeroSlot())
        meanStatistics.forEach((stat) =>
          slots.push({ kind: "stat", field: null, stat, display: MEAN_STAT_NAMES[stat] })
        )
      }
    })
    if (rowLines.length === 0) {
      const tried = resolved.rows.some((r) => (r.kind === "option" && !r.hidden) || r.kind === "net")
      return { skip: tried ? ALL_LEFT_OUT : "The table has no rows." }
    }

    lines.push(`QTABLE ${own.rows}`, ...rowLines)
    if (base.blankFillRows) lines.push(`BLANKFALSE ${own.rows}`)
    temps.push(own.rows)
    cleanup.push(own.rows)
    mainTable(own, own.rows, xtabStats(), true)

    /** Takes the mean and every difference row out, before the mean is compiled; the sentence says what went. */
    const dropScores = (sentence: (gone: RowSlot[]) => string): void => {
      const gone = slots.filter(isScoreSlot)
      for (let i = slots.length - 1; i >= 0; i--) if (isScoreSlot(slots[i])) slots.splice(i, 1)
      wantsMean = false
      said.push(sentence(gone))
    }
    const shownAs = table.kind === "scale" ? "a scale" : "single choice"
    // The words promise rows that add to 100, which fields that may all be true cannot keep.
    const mayNotAdd = `${variable} is a set of yes or no fields from the data. Shown as ${shownAs}, its rows may not add to 100.`
    /** The table shows a mean or a difference row: scores that need one category per respondent. */
    const scored = slots.some(isScoreSlot)
    // "single" from the codebook stands without a check when no preparation
    // ran, and on a table with no score either way (its rows are the same).
    const trusted = answers === "single" && (!prepared || !scored)
    if (fromData && choice && !trusted) {
      // The check (facts.md, fact 12): who in the base has none of the fields
      // marked, or several, over every field of the instance. Its outcome is
      // read when the table is built (`runtime/buildResult.ts`). "multi": the
      // codebook says several may be marked, so there is nothing to check.
      const sum = answers === "multi" ? null : checkSum(instance, catalogue, base.effective === "all")
      if (sum) {
        const none = ref(`${own.check}//None`)
        const several = ref(`${own.check}//Several`)
        lines.push(
          `QTABLE ${own.check}`,
          `FIELD ${none} BOOL`,
          `CALC ${none} "(${sum}) == 0"`,
          `FIELD ${several} BOOL`,
          `CALC ${several} "(${sum}) > 1"`,
          `XTAB ${own.checkResult} ${own.check} ${TOTAL_TABLE}${clause} OPERATORS: "n"`,
          `OUTPUT ${own.checkResult}`,
          `REMOVE ${own.checkResult}`
        )
        temps.push(own.check, own.checkResult)
        cleanup.push(own.check)
        outputs.push("check")
        checked = { kind: table.kind === "scale" ? "scale" : "categorical" }
      } else {
        // Several fields may be marked (the codebook says so), or the engine
        // cannot check it (a field cannot go into the script): the mean and
        // every difference row are left out, on single choice as on a scale.
        if (scored)
          dropScores((gone) => {
            if (answers === "multi") return noScores(gone)
            const rows = leftOutRows(gone) ?? { what: "The mean", verb: "is" }
            return `${rows.what} ${rows.verb} left out because not every category of ${variable} can be read, so the engine cannot check that each respondent has exactly one.`
          })
        if (table.kind === "categorical" || !scored) said.push(mayNotAdd)
      }
    }

    if (wantsMean) {
      const expression = resolved.scores
        ? scoreExpression(resolved.scores, usablePaths(instance, catalogue), order)
        : null
      if (!expression) {
        said.push("No scores could be worked out, so the mean was left out.")
        // The mean's slots: the mean and the statistics under it.
        for (let i = slots.length - 1; i >= 0; i--) if (slots[i].kind === "stat") slots.splice(i, 1)
      } else {
        lines.push(
          `FIELD ${ref(own.score)} ${scoreFieldType(resolved.scores ?? {})}`,
          `CALC ${ref(own.score)} "${expression}"`
        )
        temps.push(own.score)
        cleanup.push(own.score)
        meanTables(own, ref(own.score), [...MEAN_STATS, ...meanStatistics], true)
        if (excl0) meanExcl0Tables(own, ref(own.score))
      }
    }
    return { layout: slots, check: checked }
  }

  if (siblingTable) {
    const shape: SiblingLayout = table.siblingLayout === "row" ? "row" : "stacked"
    const chosen = shape === "row" ? (table.siblingRow?.trim() ?? "") : ""
    if (shape === "row" && !chosen) return skip("No row is chosen for this table.", warnings)
    /** The names the siblings kept so far are shown under. */
    const displays: string[] = []
    /**
     * The name a sibling is shown under: its shown label, or its raw label
     * when the shown one cannot be used; null when neither can. The raw
     * labels of the siblings differ, and a shown label gives way to them.
     */
    const siblingDisplay = (i: CatalogueInstance): string | null => {
      const shown = instanceName(i)
      if (shown !== i.label) {
        const others = [...displays, ...instances.filter((u) => u !== i).map((u) => u.label)]
        const refused = refusedName(shown, others, "row", "display")
        if (!refused) return shown
        warnings.push(`The shown label "${shown}" of "${i.label}" was not used. ${refused}`)
      }
      const refused = refusedName(i.label, displays, "row", "display")
      if (!refused) return i.label
      warnings.push(`"${i.label}" was left out. ${refused}`)
      return null
    }
    const parts: SiblingPart[] = []
    /** What each sibling's table said, named after the sibling unless every sibling said it. */
    const said: { display: string; list: string[] }[] = []
    /** The siblings whose table cannot be shown, and why. */
    const leftOut: { display: string; reason: string }[] = []
    for (const [index, i] of instances.entries()) {
      if (!present.includes(i)) {
        warnings.push(`"${instanceName(i)}" is not in the loaded data and was left out.`)
        continue
      }
      const display = siblingDisplay(i)
      if (display === null) continue
      const list: string[] = []
      const start = outputs.length
      // The names of the table with the sibling's place: each sibling's are
      // its own, and the script removes every one of them.
      const built = choiceTable(i, tableNames(`${table.id}_s${index + 1}`), list)
      said.push({ display, list })
      if ("skip" in built) {
        leftOut.push({ display, reason: built.skip })
        continue
      }
      const at = (role: OutputRole): number | null => {
        const found = outputs.indexOf(role, start)
        return found < 0 ? null : found
      }
      displays.push(display)
      const meanExcl0 = at("mean-excl-0")
      parts.push({
        id: i.id,
        display,
        outputs: {
          main: start,
          check: at("check"),
          mean: at("mean"),
          meanTotal: at("mean-total"),
          ...(meanExcl0 !== null
            ? { meanExcl0, meanExcl0Total: at("mean-excl-0-total") }
            : {}),
        },
        layout: built.layout,
        check: built.check,
      })
    }
    // With every sibling left out for one reason, the table is skipped with it alone.
    const reason = leftOut[0]?.reason ?? ALL_LEFT_OUT
    const oneReason = parts.length === 0 && leftOut.every((l) => l.reason === reason)
    if (!oneReason) leftOut.forEach((l) => warnings.push(`"${l.display}" was left out. ${l.reason}`))
    warnings.push(...siblingSentences(said))
    if (parts.length === 0) return skip(reason, warnings)
    if (shape === "row") {
      const found = parts.map((p) => slotNamed(p.layout, chosen)).find((s) => s !== undefined)
      if (!found || found.kind === "base") {
        const named = q.options.some((o) => o.label === chosen) ? names.option(q.label, chosen) : chosen
        return skip(`The row ${named} is not in this table.`, warnings)
      }
      title = `${title} - ${found.display}`
    }
    siblings = { layout: shape, row: shape === "row" ? chosen : null, parts }
  } else if (table.kind === "numeric") {
    const instance = present[0]
    if (unsafeText(instance.path))
      return skip(`${variable} has a character the engine cannot take.`, warnings)
    if (unsafeRef(instance.path)) return skip(`${variable} has a name that cannot be used.`, warnings)
    const x = ref(instance.path)
    // The statistics count people with a value, whatever the rule: the base
    // text says so. "Base: all answering ..." says it already.
    if (!table.baseText?.trim() && base.effective !== "answered")
      baseText = `${base.baseText} who gave a value`
    // The rows above the statistics, in one table: each value given, then
    // the ranges. A row is a yes or no field, missing where the number is,
    // so the rows' base is the people with a value, as the statistics' is.
    const rowLines: string[] = []
    const rowSlots: RowSlot[] = []
    /** The names of the rows kept so far: each is a label in the engine. */
    const taken: string[] = []
    // The values and the ranges are in the order of the numbers: sorting
    // never moves them (a value is never put back either way).
    const addRow = (name: string, expression: string, value: boolean): void => {
      const field = ref(`${n.rows}//${name}`)
      rowLines.push(`FIELD ${field} BOOL`, `CALC ${field} "${expression}"`)
      rowSlots.push({
        kind: "row",
        label: name,
        display: name,
        net: false,
        ...(value ? { value: true as const } : { fixed: true }),
      })
      taken.push(name)
    }
    if (table.values) {
      // What the values check of the session found (`runtime/valueCheck.ts`);
      // until it is in, the table waits, its base text as it will run. The
      // check runs in a ready session only: the sentence holds while the
      // session starts, and while it is in error, until it is restarted.
      const found = catalogue.valueRanges?.[instance.path]
      if (found === undefined)
        return {
          ...skip(
            catalogue.knownPaths
              ? `The values of ${variable} are read once the data session is ready.`
              : `The values of ${variable} are read once the data is imported.`,
            warnings
          ),
          baseText,
        }
      if (found === "failed")
        warnings.push(
          `The values of ${variable} could not be read, so each value is not shown. Choose Restart session to read them again.`
        )
      else if (found === "empty") warnings.push(`Nobody has a value of ${variable}, so no value is shown.`)
      else if (!found.whole || !Number.isInteger(found.min) || !Number.isInteger(found.max))
        warnings.push(`${variable} is stored as a decimal number, so each value cannot be shown. Add ranges instead.`)
      else if (found.max - found.min > MOST_VALUE_SPAN)
        warnings.push(
          `${variable} has values from ${found.min} to ${found.max}, too many to show each one. Add ranges instead.`
        )
      // A value nobody gave is dropped by the engine and stays out.
      else for (let v = found.min; v <= found.max; v++) addRow(String(v), `${x} == ${v}`, true)
    }
    const ranges: RangeItem[] = []
    resolved.rows.forEach((r) => {
      if (r.kind !== "range") return
      const name = r.name.trim()
      const refused = refusedName(name, taken)
      if (refused) {
        warnings.push(`The range "${name}" was left out. ${refused}`)
        return
      }
      const problem = boundsProblem(r.min, r.max)
      if (problem) {
        warnings.push(`The range "${name}" ${problem} and was left out.`)
        return
      }
      addRow(name, rangeExpression(instance.path, r.min, r.max), false)
      ranges.push(r)
    })
    warnings.push(...rangeWarnings(ranges))
    const rowed = rowLines.length > 0
    // Without its own list the table shows the defaults' and the statistics under the mean (`resolveTable`).
    const chosen = NUMERIC_STATS.filter((s) => resolved.statistics.includes(s.stat))
    const shown = chosen.length > 0 || excl0 ? chosen : NUMERIC_FALLBACK
    // With rows, a weighted table's statistics also give the weighted base,
    // which stands in for the rows' when the engine drops it (one row left).
    const rowBases = rowed && spec.data.weight ? [WEIGHTED_BASE] : []
    const asked = [...new Set([STATISTIC_TYPE.BASE_N, ...rowBases, ...shown.map((s) => s.stat)])]
    if (rowed) {
      // The rows with their counts and percentages, tested as a choice table's.
      lines.push(`QTABLE ${n.rows}`, ...rowLines)
      temps.push(n.rows)
      cleanup.push(n.rows)
      mainTable(n, n.rows, xtabStats(), true)
      layout.push({ kind: "base", stats: true }, ...rowSlots)
    } else mainTable(n, TOTAL_TABLE, [STATISTIC_TYPE.N], false)
    meanTables(n, x, asked, asked.includes(STATISTIC_TYPE.AVG))
    if (excl0) meanExcl0Tables(n, x)
    // The mean excluding 0 goes where it goes under a mean: right after it.
    NUMERIC_STATS.forEach((s) => {
      const mean = s.stat === STATISTIC_TYPE.AVG
      // With rows, the Base above them is the table's base.
      if (rowed && s.stat === STATISTIC_TYPE.BASE_N) return
      if (shown.includes(s))
        layout.push({
          kind: "stat",
          field: null,
          stat: s.stat,
          display: mean && excl0 ? MEAN_INCLUDING_ZERO_NAME : s.display,
        })
      if (mean && excl0) layout.push(excludingZeroSlot())
    })
  } else if (table.kind === "grid-summary") {
    const choice = resolved.summary ?? legacySummary(q, spec.defaults)
    const numberQuestion = isNumberQuestion(q)
    const optionName = (label: string): string => names.option(q.label, label)
    if (!canSummarise(q))
      return skip(`${variable} has no grid rows or loop iterations to summarise.`, warnings)
    if (choice.kind === "sum" && !numberQuestion)
      return skip(`${variable} is not a number, so it has no sum.`, warnings)
    if (numberQuestion && choice.kind !== "mean" && choice.kind !== "sum")
      return skip(`${variable} is a number: its summary shows the mean or the sum.`, warnings)
    const scored = !numberQuestion && choice.kind !== "answer" && choice.kind !== "net"
    if (scored && !resolved.scores)
      return skip("No scores could be worked out for this question, so there is no summary.", warnings)
    const scores = resolved.scores ?? {}
    if (choice.kind === "nps" && !isNps(scores))
      return skip(`${variable} is not scored 0 to 10, so it has no NPS.`, warnings)

    // The rows: the instances of the table's slice (every instance without
    // one), less those it hides.
    const { slice } = table
    const sliced = slice ? instances.filter((i) => inSlice(i, slice)) : instances
    if (slice && sliced.length === 0)
      return skip(`${variable} no longer has "${sliceWords(q, slice, names)}".`, warnings)
    const hidden = new Set(table.hiddenRows ?? [])
    const shownRows = sliced.filter((i) => !hidden.has(i.id))
    if (shownRows.length === 0) return skip("Every grid row of this table is hidden.", warnings)
    // A table fixed at some values names each row by its parts where the
    // rows run ("Online"); otherwise by the instance ("Price - Online").
    const byParts = slice !== undefined && q.dimensions.length > 1
    const rawName = (i: CatalogueInstance): string =>
      byParts ? slicedRowName(q, i, slice) : i.label
    const shownName = (i: CatalogueInstance): string =>
      byParts ? slicedRowName(q, i, slice, names) : instanceName(i)

    const usable: CatalogueInstance[] = []
    shownRows.forEach((i) => {
      const refused = refusedName(i.label, usable.map((u) => u.label))
      if (!present.includes(i))
        warnings.push(`"${shownName(i)}" is not in the loaded data and was left out.`)
      else if (unsafeText(i.label) || unsafeText(i.path))
        warnings.push(`"${shownName(i)}" has a character the engine cannot take and was left out.`)
      else if (refused) warnings.push(`"${shownName(i)}" was left out. ${refused}`)
      else if (unsafeRef(i.path))
        warnings.push(`"${shownName(i)}" has a name that cannot be used and was left out.`)
      else usable.push(i)
    })
    if (usable.length === 0) return skip(ALL_LEFT_OUT, warnings)
    /**
     * The names the grid rows kept so far are shown under; a sum summary's
     * first row is its Total.
     */
    const displays: string[] = choice.kind === "sum" ? [SUM_TOTAL_NAME] : []
    /**
     * The name a grid row is shown under: its shown name, or its raw name
     * when the shown one cannot be used; null when neither can (a row whose
     * raw name is the Total of a sum summary). The engine keeps the raw
     * label. The raw names are told apart above, and a shown name gives way
     * to every other row's raw name.
     */
    const rowDisplay = (i: CatalogueInstance): string | null => {
      const raw = rawName(i)
      const shown = shownName(i)
      if (shown !== raw) {
        const others = [...displays, ...usable.filter((u) => u !== i).map(rawName)]
        const refused = refusedName(shown, others, "row", "display")
        if (!refused) return shown
        warnings.push(`The shown label "${shown}" of "${raw}" was not used. ${refused}`)
      }
      const refused = refusedName(raw, displays, "row", "display")
      if (!refused) return raw
      warnings.push(`"${raw}" was left out. ${refused}`)
      return null
    }

    const weighted = !!spec.data.weight
    // A weighted table shows both bases: the count and the weighted count.
    const baseStats = weighted ? [STATISTIC_TYPE.BASE_N, WEIGHTED_BASE] : [STATISTIC_TYPE.BASE_N]
    // Each row's base is shown above its value when the statistics name the base.
    const bases = resolved.statistics.includes(STATISTIC_TYPE.BASE_N) ? { bases: baseStats } : {}
    const summaryStats = (shown: string[]): string[] => [
      STATISTIC_TYPE.BASE_N,
      ...shown,
      ...(weighted ? [WEIGHTED_BASE] : []),
    ]
    /** The field of each grid row in the summary table `own`, by its raw label. */
    const rowField = (own: string, label: string): string => ref(`${own}//${label}`)
    // The Base row, counted over the total table (both counts when
    // weighted): everyone in the table's population.
    const everyone = weighted ? [STATISTIC_TYPE.N, STATISTIC_TYPE.BIG_N] : [STATISTIC_TYPE.N]
    layout.push({ kind: "base", total: true })
    /**
     * The universe of the Base row. Under base "answered" it is those of the
     * table's population who answered at least one of its rows (one of
     * `fields`, one per row, has a value), as the base line says; otherwise
     * the table's own.
     */
    const baseClause = (fields: string[]): string => {
      if (base.effective !== "answered") return clause
      const any = `(${fields.map((f) => `NOT ISMISSING(${f})`).join(" OR ")})`
      const field = ref(n.baseUniverse)
      lines.push(
        `FIELD ${field} BOOL`,
        `CALC ${field} "${base.expression ? `${base.expression} AND ${any}` : any}"`
      )
      temps.push(n.baseUniverse)
      cleanup.push(n.baseUniverse)
      return ` UNIVERSE:${field}`
    }
    /** The field of each grid row kept, which the Base row reads under "answered". */
    const known: string[] = []

    if (choice.kind === "mean" || choice.kind === "sum") {
      // What each grid row shows. A summary of means: the mean, the
      // statistics under it, or both, a row for each ("<row> - Mean",
      // "<row> - Standard error"); the title names them all: "- Mean",
      // "- Standard error", "- Mean and standard error". A sum: the row's sum
      // and its share of the Total, after the Total.
      const perRow =
        choice.kind === "sum"
          ? [{ stat: STATISTIC_TYPE.SUM, display: "Sum" }]
          : meanSummaryStats(resolved.statistics)
      const shows = perRow.map((s, i) => (i === 0 ? s.display : s.display.toLowerCase()))
      title = `${title} - ${listOf(shows, "and")}`
      if (choice.kind === "sum")
        layout.push({
          kind: "stat",
          field: null,
          stat: STATISTIC_TYPE.SUM,
          display: SUM_TOTAL_NAME,
          sum: "total",
        })
      lines.push(`QTABLE ${n.scores}`)
      temps.push(n.scores)
      cleanup.push(n.scores)
      usable.forEach((i) => {
        const field = rowField(n.scores, i.label)
        let lost: { label: string; loss: Loss }[] = []
        let fieldLines: string[]
        if (numberQuestion) {
          // ASSIGN refuses an INT source for a REAL field; CALC converts and keeps missing values.
          fieldLines = [`FIELD ${field} REAL`, `CALC ${field} "${ref(i.path)}"`]
        } else {
          const picked = pickOptions(i, order.filter((l) => l in scores), catalogue)
          const paths = Object.fromEntries(picked.used.map((u) => [u.label, u.path]))
          const expression = scoreExpression(scores, paths, order)
          if (!expression) {
            warnings.push(`"${shownName(i)}" ${rowLeftOut("scored option", picked.lost)} and was left out.`)
            return
          }
          lost = picked.lost
          fieldLines = [`FIELD ${field} ${scoreFieldType(scores)}`, `CALC ${field} "${expression}"`]
        }
        const display = rowDisplay(i)
        if (display === null) return
        displays.push(display)
        lost.forEach(({ label, loss }) =>
          warnings.push(
            `"${optionName(label)}" ${LOSS[loss]}, so the mean of "${display}" was worked out without it.`
          )
        )
        lines.push(...fieldLines)
        known.push(field)
        perRow.forEach((s, k) =>
          layout.push({
            kind: "stat",
            field: i.label,
            stat: s.stat,
            display: perRow.length === 1 ? display : `${display} - ${s.display}`,
            ...(k === 0 ? bases : {}),
            ...(choice.kind === "sum" ? { sum: "row" as const } : {}),
          })
        )
      })
      if (displays.length === (choice.kind === "sum" ? 1 : 0)) return skip(ALL_LEFT_OUT, warnings)
      const asked = perRow.map((s) => s.stat)
      mainTable(n, TOTAL_TABLE, everyone, false, baseClause(known))
      // Only the mean is tested: without it there is nothing to test. A sum never is.
      meanTables(n, n.scores, summaryStats(asked), asked.includes(STATISTIC_TYPE.AVG))
    } else {
      // Each grid row is a yes or no field per share it shows (NPS: the
      // promoters and the detractors), missing for those who did not answer
      // it, so each row has its own base.
      const nps = choice.kind === "nps"
      const parts = ((): { name: string; members: string[] }[] | string => {
        switch (choice.kind) {
          case "top":
          case "bottom":
            return [{ name: "", members: boxMembers(q, scores, choice.size, choice.kind) }]
          case "middle": {
            const { topBox, bottomBox } = spec.defaults.scale
            const members = middleMembers(q, scores, topBox, bottomBox)
            return members.length > 0
              ? [{ name: "", members }]
              : `Every scored answer of ${variable} is in the top or the bottom box, so there is no middle.`
          }
          case "answer":
            return q.options.some((o) => o.label === choice.label)
              ? [{ name: "", members: [choice.label] }]
              : `"${optionName(choice.label)}" is no longer an answer of ${variable}.`
          case "net": {
            const known = choice.members.filter((m) => q.options.some((o) => o.label === m))
            choice.members
              .filter((m) => !known.includes(m))
              .forEach((m) =>
                warnings.push(
                  `"${m}" no longer exists, so the net "${choice.name.trim()}" was built without it.`
                )
              )
            return known.length > 0
              ? [{ name: "", members: known }]
              : `The net "${choice.name.trim()}" has no option that still exists.`
          }
          default:
            return [
              { name: NPS_PROMOTERS, members: labelsInRange(q, scores, 9, 10) },
              { name: NPS_DETRACTORS, members: labelsInRange(q, scores, 0, 6) },
            ]
        }
      })()
      if (typeof parts === "string") return skip(parts, warnings)
      const named = summaryName(choice, optionName)
      title = `${title} - ${named}`
      /** What a grid row is left out for when it has none, per part. */
      const missing = (part: { name: string }): string => {
        switch (choice.kind) {
          case "top":
            return "top box option"
          case "bottom":
            return "bottom box option"
          case "middle":
            return "middle option"
          case "answer":
            return `"${named}" answer`
          case "net":
            return `option of the net "${named}"`
          default:
            return part.name === NPS_PROMOTERS ? "promoter option" : "detractor option"
        }
      }
      lines.push(`QTABLE ${n.rows}`)
      temps.push(n.rows)
      cleanup.push(n.rows)
      usable.forEach((i) => {
        const picked = parts.map((p) => ({ ...p, ...pickOptions(i, p.members, catalogue) }))
        const empty = picked.find((p) => p.used.length === 0)
        if (empty) {
          warnings.push(`"${shownName(i)}" ${rowLeftOut(missing(empty), empty.lost)} and was left out.`)
          return
        }
        const display = rowDisplay(i)
        if (display === null) return
        displays.push(display)
        const fields = picked.map((p) => {
          p.lost.forEach(({ label: option, loss }) =>
            warnings.push(
              nps
                ? `"${optionName(option)}" ${LOSS[loss]}, so the ${p.name.toLowerCase()} of "${display}" were counted without it.`
                : `"${optionName(option)}" ${LOSS[loss]}, so the net "${display}" was built without it.`
            )
          )
          const label = p.name ? `${i.label} - ${p.name}` : i.label
          const field = rowField(n.rows, label)
          lines.push(
            `FIELD ${field} BOOL`,
            `CALC ${field} "(${p.used.map((u) => ref(u.path)).join(" OR ")})"`
          )
          return label
        })
        // The promoters and the detractors have the same people: one field is enough.
        known.push(rowField(n.rows, fields[0]))
        layout.push(
          nps
            ? { kind: "difference", display, plus: fields[0], minus: fields[1], fields: true, ...bases }
            : { kind: "stat", field: fields[0], stat: STATISTIC_TYPE.PERC, display, ...bases }
        )
      })
      if (displays.length === 0) return skip(ALL_LEFT_OUT, warnings)
      // "all": nobody drops out of a row for a missing answer.
      if (base.blankFillRows) lines.push(`BLANKFALSE ${n.rows}`)
      mainTable(n, TOTAL_TABLE, everyone, false, baseClause(known))
      // NPS is a difference of two shares: it is never tested.
      meanTables(n, n.rows, summaryStats([STATISTIC_TYPE.PERC]), !nps)
    }
    // Each row has its own base, the people who answered it; under "all", a
    // share counts everyone (the rows filled with false) and a mean or a sum
    // those who gave a value. The base text says so.
    if (!table.baseText?.trim())
      baseText = `${base.baseText}; each row counts ${
        base.effective !== "all"
          ? "those who answered it"
          : choice.kind === "mean" || choice.kind === "sum"
            ? "those who gave a value"
            : "all of them"
      }`
  } else if (isYesNoField(q)) {
    // One yes or no field: a top-level BOOL, or a TABLE of one BOOL leaf. A
    // table of one row gets no Totals block from the engine (facts.md, facts
    // 1 and 7), so its rows are Yes, a one-member net (false OR missing stays
    // missing, fact 2), and No, its negation (NOT missing stays missing, fact
    // 4). Stored rows do not apply.
    const found = optionPath(present[0], q.options[0].label, catalogue)
    if ("loss" in found) return skip(`${variable} ${LOSS[found.loss]}.`, warnings)
    const { path } = found
    const yes = ref(`${n.rows}//Yes`)
    const no = ref(`${n.rows}//No`)
    lines.push(
      `QTABLE ${n.rows}`,
      `FIELD ${yes} BOOL false`,
      `OR ${yes} ${ref(path)}`,
      `FIELD ${no} BOOL`,
      `CALC ${no} "NOT (${ref(path)})"`
    )
    if (base.blankFillRows) lines.push(`BLANKFALSE ${n.rows}`)
    temps.push(n.rows)
    cleanup.push(n.rows)
    layout.push({ kind: "base" })
    // Always Yes, then No: sorting never moves them.
    ;["Yes", "No"].forEach((label) =>
      layout.push({ kind: "row", label, display: label, net: false, fixed: true })
    )
    mainTable(n, n.rows, xtabStats(), true)
  } else {
    const built = choiceTable(present[0], n, warnings)
    if ("skip" in built) return skip(built.skip, warnings)
    layout.push(...built.layout)
    check = built.check
  }

  cleanup.forEach((name) => lines.push(`REMOVE ${name}`))
  if (base.expression) lines.push(`REMOVE ${n.universe}`)

  return {
    tableId: table.id,
    status: "run",
    script: lines.join("\n"),
    outputs,
    temps,
    banner,
    layout,
    significance: tested,
    title,
    baseText,
    filterText: base.filterText,
    weightText,
    warnings,
    check,
    sort: resolved.sort,
    hideEmpty: resolved.hideEmpty,
    ...(siblings ? { siblings } : {}),
  }
}

/**
 * The plan of one table of `spec`; null for a table it does not have. One
 * `ShownNames` goes through every step, so `seen` (when given) ends up
 * holding every variable whose names the plan read: its own, the banner's,
 * the filter's and the book filter's, the routing condition's and the
 * weight. A plan changes with the shown labels of those variables only.
 */
export const compileSpecTable = (
  tableId: string,
  spec: TabSpecDocument,
  catalogue: Catalogue,
  seen?: Set<string>
): TablePlan | null => {
  const table = spec.tables.find((t) => t.id === tableId)
  if (!table) return null
  const names = shownNames(spec.labels, seen)
  return compileTable(resolveTable(table, spec, catalogue, names), spec, catalogue, names)
}
